(ns common.search.capabilities)

;; --- Capability key contracts (documented and enforced) ---
(def capability-keys
  [:search/indexed?
   :search/full-text?
   :search/sortable?
   :search/facet?
   :search/filter
   :search/large-text?
   :search/high-cardinality?
   :search/materialized])

(def filter-capability-keys
  [:terms? :prefix? :range?])

(def default-fallback-capabilities
  {:search/indexed? false
   :search/filter   {:terms? false :prefix? false :range? false}})

(def default-capabilities-by-type
  {;; --- BOOLEANS ---
   :data.type/boolean
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     true
    :search/filter     {:terms? true :prefix? false :range? false}}

   ;; --- TEXT FAMILY ---
   ;; short/plain text (names, titles). full-text + keyword + lower.
   :data.type/text
   {:search/indexed?    true
    :search/full-text?  true
    :search/sortable?   true                                                                        ; via .keyword
    :search/facet?      false                                                                       ; usually high-cardinality; opt-in if needed
    :search/filter      {:terms?  true                                                              ; exact match via .keyword
                         :prefix? true                                                              ; via .lower + prefix/wildcard
                         :range?  false}
    :search/large-text? false}

   ;; short/plain text explicitly marked “simple”
   :data.type/text-simple
   {:search/indexed?    true
    :search/full-text?  true
    :search/sortable?   true
    :search/facet?      false
    :search/filter      {:terms? true :prefix? true :range? false}
    :search/large-text? false}

   ;; long text (notes). index for full-text, but **omit from grid _source by default**.
   :data.type/text-multiline
   {:search/indexed?    true
    :search/full-text?  true
    :search/sortable?   false                                                                       ; generally not useful to sort
    :search/facet?      false
    :search/filter      {:terms? false :prefix? false :range? false}
    :search/large-text? true}

   ;; richtext (HTML). index for full-text (after sanitization), not sortable/facet.
   :data.type/text-richtext
   {:search/indexed?    true
    :search/full-text?  true
    :search/sortable?   false
    :search/facet?      false
    :search/filter      {:terms? false :prefix? false :range? false}
    :search/large-text? true}

   ;; lists of text/urls/emails — behave like text for search; UI decides editor
   :data.type/text-multi
   {:search/indexed?    true
    :search/full-text?  true
    :search/sortable?   false
    :search/facet?      false
    :search/filter      {:terms? true :prefix? true :range? false}
    :search/large-text? true}

   ;; --- EMAILS ---
   ;; single email → keyword semantics + case-insensitive copy + optional full-text off
   :data.type/email
   {:search/indexed?   true
    :search/full-text? false                                                                        ; no need for BM25 on emails
    :search/sortable?  true
    :search/facet?     true
    :search/filter     {:terms?  true                                                               ; exact
                        :prefix? true                                                               ; prefix on .lower (fast “starts with”)
                        :range?  false}}

   :data.type/email-multi
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  false
    :search/facet?     true
    :search/filter     {:terms? true :prefix? true :range? false}}

   ;; --- URLS ---
   :data.type/text-url
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     false
    :search/filter     {:terms? true :prefix? true :range? false}}

   :data.type/url-multi
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  false
    :search/facet?     false
    :search/filter     {:terms? true :prefix? true :range? false}}

   ;; --- KEYWORDS / ENUMS ---
   :data.type/keyword
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     true
    :search/filter     {:terms? true :prefix? true :range? false}}

   :data.type/keyword-multi
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  false
    :search/facet?     true
    :search/filter     {:terms? true :prefix? true :range? false}}

   ;; --- NUMBERS / PERCENT / MULTIPLIER / CURRENCY ---
   :data.type/integer
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     false
    :search/filter     {:terms? true :prefix? false :range? true}}

   :data.type/long              :data.type/integer
   :data.type/number            :data.type/integer
   :data.type/percentage        :data.type/integer
   :data.type/multiplier        :data.type/integer

   ;; currency value (numeric) and symbol (enum)
   :data.type/currency-value    :data.type/integer
   :data.type/currency-symbol
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     true
    :search/filter     {:terms? true :prefix? true :range? false}}

   ;; --- DATES ---
   :data.type/date
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     false
    :search/filter     {:terms? true :prefix? false :range? true}}

   :data.type/datetime          :data.type/date
   :data.type/date-year
   {:search/indexed?   true
    :search/full-text? false
    :search/sortable?  true
    :search/facet?     true                                                                         ; year buckets are handy
    :search/filter     {:terms? true :prefix? false :range? true}}

   ;; --- REFS / FILES ---
   :data.type/ref-single
   {:search/indexed?   false                                                                        ; expose materialized logical fields instead
    :search/full-text? false :search/sortable? true :search/facet? false
    :search/filter     {:terms? false :prefix? false :range? false}}

   :data.type/ref-multi
   {:search/indexed?   false
    :search/full-text? false :search/sortable? false :search/facet? true                            ; sometimes facet by count/presence
    :search/filter     {:terms? true :prefix? false :range? false}}

   :data.type/file
   {:search/indexed?   false
    :search/full-text? false :search/sortable? false :search/facet? false
    :search/filter     {:terms?  true                                                               ; presence/ids if you materialize
                        :prefix? false :range? false}}

   ;; --- COMPLEX / BLOB ---
   :data.type/text-edn
   {:search/indexed?   false                                                                        ; store only; selectively materialize paths
    :search/full-text? false :search/sortable? false :search/facet? false
    :search/filter     {:terms? false :prefix? false :range? false}}

   :data.type/financials        :data.type/text-edn
   :data.type/tesi-financials   :data.type/text-edn
   :data.type/tesi-financials-2 :data.type/text-edn})

(defn derive-capabilities
  "Given a field-registry entry that includes :field/data-type, return abstract search capabilities.
   Per-field overrides (e.g., {:search/facet? false}) are merged on top."
  [field-meta]
  (let [data-type (:field/data-type field-meta)
        default-capabilities (get default-capabilities-by-type data-type
                                  default-fallback-capabilities)
        raw-overrides (select-keys field-meta capability-keys)
        sanitized-overrides (cond-> raw-overrides
                              (:search/filter raw-overrides)
                              (update :search/filter #(select-keys % filter-capability-keys)))]
    (merge default-capabilities sanitized-overrides)))
